import { useEffect, useState, useCallback } from "react"
import AddMachineModel from "../components/MachineManagement";
import { pmndpAPI } from "../../../services/pmndp.api";
import { PMNDPTableSkeleton } from '../components/PMNDPLoader';

import styles from '../components/pmndp-enhanced.module.css';
import '../components/pmndp-enhanced.css'; // Temporary fix

// Constants
const HIGHLIGHT_TYPES = ["HBV", "HCV", "HIV", "Double-Positive"];

// Status mapping function
const getStatusInfo = (status, labels) => {
    switch (status) {
        case 1:
            return { label: labels.functional, class: "pmndp-status-functional" };
        case 2:
            return { label: labels.nonFunctional, class: "pmndp-status-non-functional" };
        case 3:
            return { label: labels.condemn, class: "pmndp-status-non-functional" };
        case 4:
            return { label: labels.wrongEntry, class: "pmndp-status-non-functional" };
        default:
            return { label: "", class: "" };
    }
};

/**
 * MachineMaster component for managing dialysis machines
 * Provides functionality to view, search, and edit machine records
 * @returns {JSX.Element} The machine master management interface
 */
export const MachineMaster = () => {

    const labels = {
        machineList: "Machine List",
        addNewMachine: "Add New Machine",
        serialNo: "Serial No :",
        search: "Search",
        reset: "Reset",
        sNo: "S.No.",
        machineName: "Machine Name",
        machineSerialNo: "Machine Serial No.",
        machineType: "Machine Type",
        machineStatus: "Machine Status",
        action: "Action",
        functional: "Functional",
        nonFunctional: "Non Functional",
        condemn: "Condemn",
        wrongEntry: "Wrong Entry",
        noMachineData: "No machine data available.",
        first: "First",
        previous: "Previous",
        next: "Next",
        last: "Last",
        page: "Page",
        of: "of"
    };

    const [addMachine, setAddMachine] = useState(false);
    const [editMachineData, setEditMachineData] = useState(null);

    const [tableData, setTableData] = useState([]); // Assuming this state is used to store the dialysis history data

    // Add search state
    const [searchSerial, setSearchSerial] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(15); // Set your page size here
    const [totalItems, setTotalItems] = useState(0);

    // Add search trigger state
    const [searchTrigger, setSearchTrigger] = useState(0);

    /**
     * Fetches machine data from the API with pagination and search functionality
     * @param {number} pageNo - Page number for pagination
     * @param {string} serial - Serial number for filtering
     */
    const getMachines = useCallback(async (pageNo = page, serial = searchSerial) => {
        try {
            setIsLoading(true);
            const pmndpUserDetails = JSON.parse(localStorage.getItem("PMNDP_loggedinUserdetails"));

            const params = { page: pageNo, pageLength: pageSize, dd: pmndpUserDetails?.facilityTypeId };
            if (serial) params.sr_no = serial;
            const responseMachine = await pmndpAPI.getMachineMaster(params);

            const machineData = responseMachine?.data?.data?.dataGet;
            const total = responseMachine?.data?.data?.totalItems || 0;

            if (Array.isArray(machineData)) {
                setTableData(machineData);
            } else {
                console.warn('No machine data received or invalid format');
                setTableData([]);
            }
            setTotalItems(total);
        } catch (error) {
            console.error("Error fetching machines:", error);
            setTableData([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, searchSerial]);

    useEffect(() => {
        getMachines(page, searchSerial);
    }, [getMachines, page, searchSerial, searchTrigger]);

    /**
     * Opens the edit modal with the selected machine data
     * @param {Object} item - Machine data object to edit
     */
    const handleEdit = (item) => {
        setEditMachineData(item);
        setAddMachine(true);
    };

    // Pagination
    const totalPages = Math.ceil(totalItems / pageSize);

    /**
     * Handles pagination page changes
     * @param {number} newPage - The new page number to navigate to
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    return (
        <div className="pmndp-enhanced-container">

            <div className="pmndp-card pmndp-fade-in">
                {/* Enhanced Header */}
                <div className="pmndp-header">
                    <div className="pmndp-header-content">
                        <div className="pmndp-header-left">
                            <div className="pmndp-icon">
                                <img src={"/images/PMNDP-logo.png"} alt="PMNDP" />
                            </div>
                            <h1 className="pmndp-title">{labels.machineList}</h1>
                        </div>
                        <button className="pmndp-add-btn" onClick={() => setAddMachine(true)}>
                            <i className="fa fa-plus"></i>
                            {labels.addNewMachine}
                        </button>
                    </div>
                </div>

                {/* Enhanced Filter Section */}
                <div className="pmndp-filter-section">
                    <div className="solt_machine">
                        <div className="pmndp-field" style={{ flex: 1 }}>
                            <label>{labels.serialNo}</label>
                            <input
                                type="text"
                                placeholder={labels.serialNo.replace(' :', '')}
                                value={searchSerial}
                                onChange={e => setSearchSerial(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                className="pmndp-btn pmndp-btn-primary"
                                onClick={e => { e.preventDefault(); setPage(1); setSearchTrigger(prev => prev + 1); }}
                            >
                                <i className="fa fa-search"></i>
                                {labels.search}
                            </button>
                            <button
                                className="pmndp-btn pmndp-btn-secondary"
                                onClick={e => { e.preventDefault(); setSearchSerial(''); setPage(1); setSearchTrigger(prev => prev + 1); }}
                            >
                                <i className="fa fa-refresh"></i>
                                {labels.reset}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Enhanced Table */}
                <div className="pmndp-table-container">
                    {isLoading ? (
                        <PMNDPTableSkeleton rows={5} columns={6} />
                    ) : (
                        <div className="table-responsive">
                            <table className="pmndp-table">
                                <thead>
                                    <tr>
                                        <th>{labels.sNo}</th>
                                        <th>{labels.machineName}</th>
                                        <th>{labels.machineSerialNo}</th>
                                        <th>{labels.machineType}</th>
                                        <th>{labels.machineStatus}</th>
                                        <th>{labels.action}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(tableData) && tableData.length > 0 ? (
                                        tableData.map((item, index) => {
                                            const statusInfo = getStatusInfo(item.machine_status, labels);
                                            const isHighlighted = HIGHLIGHT_TYPES.includes(item.machine_type);

                                            return (
                                                <tr key={item.id} className={isHighlighted ? 'pmndp-row-highlighted' : ''}>
                                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                                    <td><strong>{item.machine_name}</strong></td>
                                                    <td>{item.sr_no}</td>
                                                    <td>
                                                        <span className={isHighlighted ? 'pmndp-status-badge pmndp-status-non-functional' : ''}>
                                                            {item.machine_type}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`pmndp-status-badge ${statusInfo.class}`}>
                                                            {statusInfo.label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="pmndp-action-btn pmndp-action-btn-edit"
                                                            onClick={() => handleEdit(item)}
                                                            title="Edit Machine"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6">
                                                <div className="pmndp-empty-state">
                                                    <div className="pmndp-empty-icon">
                                                        <i className="fa fa-cogs"></i>
                                                    </div>
                                                    <div className="pmndp-empty-title">{labels.noMachineData}</div>
                                                    <div className="pmndp-empty-description">No machines have been added yet. Click "Add New Machine" to get started.</div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Enhanced Pagination */}
                    {totalPages > 1 && (
                        <div className="pmndp-pagination">
                            <button
                                className="pmndp-pagination-btn"
                                onClick={() => handlePageChange(1)}
                                disabled={page === 1 || isLoading}
                                title={labels.first}
                            >
                                &#171;
                            </button>
                            <button
                                className="pmndp-pagination-btn"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1 || isLoading}
                                title={labels.previous}
                            >
                                &#8249;
                            </button>

                            {totalPages > 0 && Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                if (totalPages <= 5) return i + 1;
                                if (page <= 3) return i + 1;
                                if (page >= totalPages - 2) return totalPages - 4 + i;
                                return page - 2 + i;
                            }).map(i => (
                                <button
                                    key={i}
                                    className={`pmndp-pagination-btn ${page === i ? 'active' : ''}`}
                                    onClick={() => handlePageChange(i)}
                                    disabled={page === i || isLoading}
                                >
                                    {i}
                                </button>
                            ))}

                            <button
                                className="pmndp-pagination-btn"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages || totalPages === 0 || isLoading}
                                title={labels.next}
                            >
                                &#8250;
                            </button>
                            <button
                                className="pmndp-pagination-btn"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={page === totalPages || totalPages === 0 || isLoading}
                                title={labels.last}
                            >
                                &#187;
                            </button>

                            <span className="pmndp-pagination-info">
                                {labels.page} {page} {labels.of} {totalPages}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {addMachine && (
                <AddMachineModel
                    onClose={() => { setAddMachine(false); setEditMachineData(null); }}
                    getMachines={getMachines}
                    editData={editMachineData}
                />
            )}

            <div className="pmndp-footer">
                <p>
                    © All rights reserved by Ministry of Health & Family Welfare,
                    Government of India.
                </p>
            </div>
        </div>
    );
};

// Export the MachineMaster component as default
export default MachineMaster;

// Backward compatibility export
export { MachineMaster as Machinemaster };
